import { graphTools } from './graph-tools'

type ExecutionMode = 'ai' | 'deterministic'
type TriggerType = 'manual' | 'schedule' | 'event' | 'chat'

export type TestInputField = {
  key: string
  label: string
  type: 'text' | 'select'
  options?: string[]
  defaultValue: string
}

export type TestInputFormState = {
  execution_mode: ExecutionMode
  prompt: string
  deterministic_graph: Record<string, unknown> | null
  trigger_type: TriggerType
  trigger_config: Record<string, unknown>
}

const SAMPLE_EVENT_PAYLOADS: Record<string, Record<string, string>> = {
  'campaign.launched': { campaign_id: 'cmp_123', campaign_name: 'Summer sale' },
  'campaign.paused': { campaign_id: 'cmp_123', campaign_name: 'Summer sale' },
  'creative.rejected': { creative_id: 'crt_456', campaign_id: 'cmp_123', reason: 'Policy violation' },
  'creative.approved': { creative_id: 'crt_456', campaign_id: 'cmp_123' },
  'budget.threshold_hit': { campaign_id: 'cmp_123', spend_pct: '0.92', budget_cents: '50000' },
  'pacing.underpacing': { campaign_id: 'cmp_123', pacing_pct: '0.55' },
  'report.weekly_ready': { report_id: 'rpt_789', period: 'last_7' },
}

function labelize(value: string) {
  return value.replace(/[._]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

function extractPromptVars(prompt: string): string[] {
  const vars = new Set<string>()
  for (const match of prompt.matchAll(/\{\{([^}]+)\}\}/g)) {
    const name = match[1].trim()
    if (name) vars.add(name)
  }
  return [...vars]
}

function schemaToFields(schema: Record<string, unknown>, prefix: string): TestInputField[] {
  const props = schema.properties as Record<string, Record<string, unknown>> | undefined
  if (!props) return []
  return Object.entries(props).map(([key, prop]) => ({
    key: prefix ? `${prefix}.${key}` : key,
    label: labelize(key),
    type: Array.isArray(prop.enum) ? 'select' : 'text',
    options: Array.isArray(prop.enum) ? prop.enum.map(String) : undefined,
    defaultValue: Array.isArray(prop.enum) ? String(prop.enum[0]) : '',
  }))
}

function getEntryToolSchema(graph: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!graph) return null
  const entryId = graph.entry
  const nodes = graph.nodes
  if (typeof entryId !== 'string' || !Array.isArray(nodes)) return null
  const entry = nodes.find((node) => {
    return !!node && typeof node === 'object' && (node as { id?: string }).id === entryId
  }) as { kind?: string; tool?: string } | undefined
  if (!entry || entry.kind !== 'tool' || typeof entry.tool !== 'string') return null
  const tool = graphTools.find((candidate) => candidate.name === entry.tool)
  return tool?.inputSchema ?? null
}

function graphUsesEvent(graph: Record<string, unknown> | null): boolean {
  if (!graph) return false
  const serialized = JSON.stringify(graph)
  return serialized.includes('$event')
}

export function deriveTestInputFields(form: TestInputFormState): TestInputField[] {
  if (form.trigger_type === 'event' || graphUsesEvent(form.deterministic_graph)) {
    const eventType = String(form.trigger_config.event_type || 'campaign.launched')
    const sample = SAMPLE_EVENT_PAYLOADS[eventType] ?? { type: eventType }
    return Object.entries(sample).map(([key, defaultValue]) => ({
      key: `event.${key}`,
      label: labelize(key),
      type: 'text',
      defaultValue,
    }))
  }

  if (form.execution_mode === 'deterministic') {
    const schema = getEntryToolSchema(form.deterministic_graph)
    if (schema) return schemaToFields(schema, '')
    return []
  }

  return extractPromptVars(form.prompt).map((key) => ({
    key,
    label: labelize(key),
    type: 'text',
    defaultValue: '',
  }))
}

function setNestedValue(obj: Record<string, unknown>, path: string, value: string) {
  const parts = path.split('.')
  let current = obj
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index]
    if (!(part in current) || typeof current[part] !== 'object' || current[part] === null) {
      current[part] = {}
    }
    current = current[part] as Record<string, unknown>
  }
  current[parts[parts.length - 1]] = value
}

export function testInputsFromFields(
  fields: TestInputField[],
  values: Record<string, string>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const field of fields) {
    const value = values[field.key] ?? field.defaultValue
    if (!value) continue

    if (field.key.startsWith('event.')) {
      const eventKey = field.key.slice('event.'.length)
      if (!result.event || typeof result.event !== 'object') result.event = {}
      ;(result.event as Record<string, unknown>)[eventKey] = value
      continue
    }

    setNestedValue(result, field.key, value)
  }

  return result
}
